<template>
  <div class="flex flex-col gap-1 mb-4">
    <div class="font-bold">
      Call Recording{{ recordings.length > 1 ? "s" : "" }}
    </div>
    <div class="transcript p-2 mt-1 flex-col gap-2">
      <div v-for="(recording, idx) in recordings" :key="recording.id">
        <div v-if="recording.transcriptParsed?.length" class="w-full">
          <div
            v-for="(line, index) in recording.transcriptParsed"
            :key="index"
            class="line w-full"
            :class="recording.transcriptParsed[0]?.user"
          >
            <div>
              <strong>{{ line.user }}: </strong><span>{{ line.message }}</span>
            </div>
          </div>
        </div>
        <div v-else>--</div>
        <div
          class="inline-flex cursor-pointer mt-2"
          @click="handleRecordingClick(recording)"
        >
          <div class="playback-link text-primary-700">
            <i class="fa-solid fa-circle-play mr-1"></i>
            Listen to Recording
          </div>
        </div>
        <hr
          v-if="idx < recordings.length - 1"
          class="my-4 border-b-none border-gray-200"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import chunk from "lodash/chunk";
import API from "serviceshift-ui/api-client";
import { computed } from "vue";
import { getProperty } from "@/lib/helpers";

import {
  CallShow,
  RecordingsEntity,
  TranscriptParsedEntity
} from "@/typings/call";

const props = defineProps<{ call: CallShow }>();

const recordings = computed<RecordingsEntity[]>(() => {
  if (!props.call.recordings?.length && !props.call.playback_url) return [];

  return (
    props.call.recordings || [
      {
        id: Date.now(),
        playback_url: props.call.playback_url || "",
        transcript: props.call.transcript || ""
      } as RecordingsEntity
    ]
  ).map((recording) => ({
    ...recording,
    transcriptParsed: parseTranscript(recording.transcript)
  }));
});

const handleRecordingClick = async (recording: any) => {
  if (props.call.links.get_source_access_token) {
    const token = await API.makeRequest(
      props.call.links.get_source_access_token,
      {
        method: "GET"
      }
    ).then((res) => getProperty(res, "data.access_token", ""));
    const url = new URL(recording.playback_url);
    url.searchParams.append("access_token", token);
    window.open(url.toString(), "_blank");
    return;
  }
  window.open(recording.playback_url, "_blank");
};

const parseTranscript = (transcript): TranscriptParsedEntity[] => {
  if (!transcript) return [];
  const transcriptMessages = transcript
    .split(new RegExp(`(Agent|Caller): `))
    .filter((message) => !!message);

  // Just in case this is not a conversation (ex: voicemail), return the message from the caller
  if (transcriptMessages.length === 1) {
    return [
      {
        user: "Caller",
        message: transcript
      }
    ];
  }
  return chunk(transcriptMessages, 2).map((row) => ({
    user: row[0] as string,
    message: row[1] as string
  }));
};
</script>

<style lang="scss" scoped>
.transcript {
  background: #fff;
  box-shadow: var(--box-shadow);
  border-radius: 4px;
  overflow: auto;
  max-height: 450px;
  .line {
    margin-bottom: 12px;
    &:last-of-type {
      margin-bottom: 0;
    }
  }
}
</style>
